/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useEffect, useRef } from 'react';
import { API_BASE_URL } from '../utils/apiBaseUrl.ts';
import ShopifySalesGrid from './ShopifySalesGrid/ShopifySalesGrid.tsx';
import { useCollection } from '../contexts/CollectionContext.tsx';
import SalesDataLastUpdated from './SalesDataLastUpdated.tsx';

const ShopifySalesDashboard = ({ salesData, firebaseToken }) => {
  const emailSentRef = useRef(false);
  
  // Get isAdmin directly from salesData (comes from custom claims)
  const isAdmin = salesData?.data?.isAdmin || false;
  
  // USE GLOBAL CONTEXT
  const { 
    userCollections: tagsArray, 
    selectedCollectionIndex: selectedTagIndex 
  } = useCollection();

  useEffect(() => {
    console.log('ShopifySalesDashboard useEffect triggered');
    console.log('salesData:', salesData);
    console.log('Selected collection index:', selectedTagIndex);
    console.log('User admin status (from custom claims):', isAdmin);
  }, [salesData, selectedTagIndex, isAdmin]);

  // Send email when non-admin user has no tags
  useEffect(() => {
    const sendNoTagsEmail = async () => {
      if (!isAdmin && salesData?.data && salesData?.data?.tags?.length < 1 && !emailSentRef.current) {
        emailSentRef.current = true;
        const userFirstName = salesData?.data?.firstname || 'First Name';
        const userLastName = salesData?.data?.lastname || 'Last Name';
        const userName = userFirstName + ' ' + userLastName;
        const userEmail = salesData?.data?.email || 'User';
        const userId = salesData?.data?.userId || 'no id';
        
        try {
          const response = await fetch(`${API_BASE_URL}/.netlify/functions/send-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: 'sherpas@merch-sherpa.com',
              subject: `Alert: User ${userName} has no tags assigned`,
              text: `${userName} logged in but has no tags/collections assigned to their account.`,
              html: `
                <h2>User Alert</h2>
                <p><strong>userId:</strong> ${userId}</p>
                <p><strong>userName:</strong> ${userName}</p>
                <p><strong>userEmail:</strong> ${userEmail}</p>
                <p><strong>Issue:</strong> No tags/collections assigned</p>
                <p>This user has logged in but currently has no sales data to display.</p>
              `,
              userName: userName
            })
          });

          const result = await response.json();
          
          if (response.ok) {
            console.log('Email sent successfully:', result);
          } else {
            console.error('Failed to send email:', result);
          }
        } catch (error) {
          console.error('Error sending email:', error);
        }
      }
    };

    sendNoTagsEmail();
  }, [salesData, isAdmin]);

  // Main render when collections are loaded
  if (tagsArray && tagsArray.length > 0) {
    console.log('tagsArray in ShopifySalesDashboard:', tagsArray);
    console.log('=== ShopifySalesDashboard Debug ===');
    console.log('isAdmin (from custom claims):', isAdmin);
    console.log('tagsArray from context:', tagsArray);
    console.log('salesData?.data?.tags:', salesData?.data?.tags);
    console.log('salesData?.data?.tagsArray:', salesData?.data?.tagsArray);
    console.log('tagsArray.length:', tagsArray?.length);

    return (
      <div id="sales">
        <h1>My Sales</h1>
        {!isAdmin && <SalesDataLastUpdated />}

        <ShopifySalesGrid 
          tagsArray={
            isAdmin 
              ? (tagsArray || salesData?.data?.tagsArray || [])
              : (salesData?.data?.tags || salesData?.data?.tagsArray || [])
          }
          isAdmin={isAdmin}
          authToken={firebaseToken}
          initialSelectedValue={selectedTagIndex}
          onSelectionChange={undefined}
          userData={salesData?.data}
        />
      </div>
    );
  }

  // Fallback rendering for debugging
  return (
    <div id="sales-debug">
      <h1>
        My Sales
        {isAdmin && (
          <>
            <span style={{ 
              marginLeft: '10px', 
              padding: '3px 10px', 
              background: '#28a745', 
              color: 'white', 
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'normal'
            }}>
              ADMIN
            </span>
          </>
        )}
      </h1>
      {!isAdmin && tagsArray?.length < 1 && (
        <h2>We have been informed that you have validated your email. We will get you setup ASAP!</h2>
      )}
      {(isAdmin && tagsArray?.length < 1) &&
        <div>
          <h3>Debug Info:</h3>
          <p>User Type: {isAdmin ? 'Admin' : 'Regular User'}</p>
          <p>tagsArray length: {tagsArray?.length || 0}</p>
          <p>Has salesData: {!!salesData}</p>
          <p>Has salesData.data: {!!salesData?.data}</p>
          {isAdmin ? (
            <>
              <p>Has tagsArray (collections): {!!salesData?.data?.tagsArray}</p>
              <p>Collections count: {salesData?.data?.tagsArray?.length || 0}</p>
            </>
          ) : (
            <>
              <p>Has salesDataByTag: {!!salesData?.data?.salesDataByTag}</p>
              <p>SalesDataByTag length: {salesData?.data?.salesDataByTag?.length || 0}</p>
            </>
          )}
          {tagsArray?.length === 0 && <p>Waiting for data to load...</p>}
        </div>
      }
    </div>
  );
};

export default ShopifySalesDashboard;